import React, { useEffect, useRef, useState } from 'react';
import Text from '../components/elements/TextComponent';
import InputField from '../components/elements/InputField';
import TextWithButton from '../components/elements/TextWithButtonComponent';
import Button from '../components/elements/Button';
import ImageComponent from '../components/elements/ImageComponent';
import userAtDesk from '../assets/userAtDesk.svg';
import { sendPasswordReset } from '../api/userApi';
import { useNavigate } from 'react-router-dom';

const PURPLE_HEIGHT = 240;

const MobileRequest: React.FC = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('');
  const [imageSize, setImageSize] = useState(160);
  const whiteSectionRef = useRef<HTMLDivElement>(null);
  const [keyboardOpen, setKeyboardOpen] = useState(false);

  useEffect(() => {
    const update = () => {
      const vh = window.visualViewport?.height || window.innerHeight;
      const keyboardHeight = window.innerHeight - vh;

      setKeyboardOpen(keyboardHeight > 100);

      const w = window.innerWidth;
      const size = Math.min(w * 0.45, vh * 0.3);
      setImageSize(Math.max(140, Math.min(size, 220)));
    };

    update();
    window.visualViewport?.addEventListener('resize', update);
    window.addEventListener('resize', update);
    return () => {
      window.visualViewport?.removeEventListener('resize', update);
      window.removeEventListener('resize', update);
    };
  }, []);

  const handleSubmit = async () => {
    if (!email) {
      setStatus('error');
      setMessage('Please enter your email address.');
      return;
    }

    try {
      const res = await sendPasswordReset(email);
      if (res.success) {
        setStatus('success');
        setMessage('A password reset email has been sent.');
      } else {
        setStatus('error');
        setMessage(res.msg || 'Failed to send reset email.');
      }
    } catch (err: any) {
      setStatus('error');
      setMessage(err.message || 'An unexpected error occurred.');
    }
  };

  return (
    <div
      style={{
        height: '100vh',
        fontFamily: 'Montserrat',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Top Purple Section */}
      <div
        style={{
          height: PURPLE_HEIGHT,
          backgroundColor: 'var(--primary)',
          padding: '24px 16px',
          display: keyboardOpen ? 'none' : 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexShrink: 0,
        }}
      >
        <ImageComponent
          src={userAtDesk}
          center
          style={{
            width: `${imageSize}px`,
            height: `${imageSize}px`,
            objectFit: 'contain',
          }}
        />
        <div>
          <Text
            text="ComeChatWithMe.ai"
            fontSize="24px"
            fontWeight="700"
            color="#ffffff"
            textAlign="center"
            navigateToHome
          />
          <Text
            text="Reset your account password"
            fontSize="14px"
            fontWeight="400"
            color="#ffffff"
            textAlign="center"
          />
        </div>
      </div>

      {/* Scrollable White Section */}
      <div
        ref={whiteSectionRef}
        style={{
          flex: 1,
          backgroundColor: '#ffffff',
          borderTopLeftRadius: 24,
          borderTopRightRadius: 24,
          overflowY: 'auto',
          padding: '24px 20px 160px',
          WebkitOverflowScrolling: 'touch',
        }}
      >
        <Text text="Forgot Password?" fontSize="24px" fontWeight="700" color="#030303" textAlign="left" />

        <div style={{ marginTop: 12 }}>
          <TextWithButton
            text="Back to "
            fontSize="14px"
            fontWeight="400"
            color="#030303"
            textAlign="left"
            showButton
            buttonLabel="login"
            onButtonClick={() => navigate('/login')}
          />
        </div>

        <div style={{ marginTop: 20 }}>
          <InputField
            text="Enter your email to receive a password reset link."
            fontSize="16px"
            fontWeight={500}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>

        {status !== 'idle' && (
          <p
            style={{
              fontFamily: 'Montserrat',
              fontSize: '14px',
              marginTop: '20px',
              color: status === 'success' ? '#2e7d32' : '#d32f2f',
              textAlign: 'left',
            }}
          >
            {message}
          </p>
        )}

        <div style={{ marginTop: 24 }}>
          <Button
            label="Send Reset Link"
            backgroundColor="var(--primary)"
            color="#ffffff"
            onClick={handleSubmit}
          />
        </div>

        <div style={{ marginTop: 24, fontSize: 14, textAlign: 'center' }}>
          <a
            href="/termsofservice"
            style={{ color: 'var(--primary)', textDecoration: 'underline', marginRight: 12 }}
          >
            Terms of Service
          </a>
          <a href="/privacypolicy" style={{ color: 'var(--primary)', textDecoration: 'underline' }}>
            Privacy Policy
          </a>
        </div>
      </div>
    </div>
  );
};

export default MobileRequest;
